import { Lock, MessageCircleQuestion, Play, Plus, Upload } from 'lucide-react';

import echo from '../../assets/alexa-echo-rest.jpg';
import '../../styles/preview-art.css';

const THUMBS = 'https://media.meutreino.fit/exercise-thumbnails/';
const THUMB = {
  bench: '00251201-Barbell-Bench-Press_Chest_thumbnail@3x.jpg',
  squat: '00431201-Barbell-Full-Squat_Thighs_thumbnail@3x.jpg',
  row: '02921201-Dumbbell-Bent-over-Row_Back_thumbnail@3x.jpg',
  curl: '02941201-Dumbbell-Biceps-Curl_Upper-Arms_thumbnail@3x.jpg',
};

const Thumb = ({ file }) => <img className="mt-art__thumb" src={`${THUMBS}${file}`} alt="" loading="lazy" />;

// The plan's three rings (protein, carbs, fat), its own progress marks
const Rings = () => {
  const arcs = [
    { r: 20, share: 0.78 },
    { r: 14.5, share: 0.62 },
    { r: 9, share: 0.5 },
  ];
  return (
    <svg className="mt-art__rings" width="48" height="48" viewBox="0 0 48 48">
      {arcs.map(({ r, share }) => {
        const length = 2 * Math.PI * r;
        return (
          <g key={r}>
            <circle cx="24" cy="24" r={r} className="mt-art__ring-track" />
            <circle
              cx="24"
              cy="24"
              r={r}
              className="mt-art__ring-arc"
              strokeDasharray={`${length * share} ${length}`}
              transform="rotate(-90 24 24)"
            />
          </g>
        );
      })}
    </svg>
  );
};

// One ring with a real share, and its number in the middle when there is one
const Ring = ({ share = 0, label }) => {
  const length = 2 * Math.PI * 18;
  return (
    <svg className="mt-art__rings" width="56" height="56" viewBox="0 0 44 44">
      <circle cx="22" cy="22" r="18" className="mt-art__ring-track" />
      {share > 0 && (
        <circle
          cx="22"
          cy="22"
          r="18"
          className="mt-art__ring-arc mt-art__ring-arc--value"
          strokeDasharray={`${length * share} ${length}`}
          style={{ '--mt-art-ring': length }}
          transform="rotate(-90 22 22)"
        />
      )}
      {label !== undefined && (
        <text x="22" y="22" className="mt-art__ring-label" textAnchor="middle" dominantBaseline="central">
          {label}
        </text>
      )}
    </svg>
  );
};

// The person's own workouts by letter when they're known, the app's naming otherwise
const Letters = ({ letters = ['A', 'B', 'C'] }) => (
  <span className="mt-art__letters">
    {letters.map((letter) => (
      <b key={letter}>{letter}</b>
    ))}
  </span>
);

// A sheet of paper: the nutritionist's plan (a line sweeps down it while it's being read)
const Sheet = ({ isReading }) => (
  <span className={`mt-art__sheet${isReading ? ' mt-art__sheet--reading' : ''}`}>
    <i className="mt-art__sheet-head" />
    <i />
    <i />
    <i className="mt-art__sheet-short" />
    <i />
  </span>
);

const ART = {
  // What the AI builds: exercises from the library
  aiWorkout: () => (
    <span className="mt-art__stack">
      <Thumb file={THUMB.bench} />
      <Thumb file={THUMB.squat} />
      <Thumb file={THUMB.row} />
    </span>
  ),
  // Workouts, by the letters the app names them with
  letters: Letters,
  // One exercise, more to add
  scratch: () => (
    <span className="mt-art__one">
      <Thumb file={THUMB.curl} />
    </span>
  ),
  rings: Rings,
  ring: Ring,
  // The Alexa on its stand
  echo: () => <img className="mt-art__photo" src={echo} alt="" loading="lazy" />,
  sheet: () => <Sheet />,
  sheetReading: () => <Sheet isReading />,
  // Meals as rows, one more to add
  rows: () => (
    <span className="mt-art__rows">
      <i />
      <i />
      <span className="mt-art__rows-add">
        <Plus />
      </span>
    </span>
  ),
  // A few weeks of training, as bars
  bars: () => (
    <span className="mt-art__bars">
      {[0.45, 0.7, 0.55, 0.9, 0.65].map((height, index) => (
        <i key={index} style={{ height: `${height * 100}%` }} />
      ))}
    </span>
  ),
  // A lesson: a video frame with its play mark
  video: () => (
    <span className="mt-art__video">
      <Play />
    </span>
  ),
  // A lesson on an exercise: a frame of the video, with its play mark
  lesson: () => (
    <span className="mt-art__video mt-art__video--frame">
      <Thumb file={THUMB.bench} />
      <Play />
    </span>
  ),
  // An email on its way
  mail: () => <span className="mt-art__mail" />,
  // What Pro gives: an exercise, the diet's rings, a workout letter
  pro: () => (
    <span className="mt-art__mix">
      <Thumb file={THUMB.bench} />
      <span className="mt-art__mix-tile">
        <Rings />
      </span>
      <b className="mt-art__mix-letter">A</b>
    </span>
  ),
};

const BADGE = { lock: Lock, plus: Plus, question: MessageCircleQuestion, upload: Upload };

/**
 * A small preview of what something makes or holds, in place of an icon on a tinted disc (owner, 30/10/2026: those
 * read as stock): exercises for the AI workout, the workout letters for a split, the plan's rings, the nutritionist's
 * sheet. `badge` marks it (a lock for Pro, a plus to add); `size` sm | md | lg; `value` is what a kind draws from
 * data (the ring's share and number). Decorative: always aria-hidden.
 */
export const PreviewArt = ({ kind, badge, size = 'md', value }) => {
  const Art = ART[kind];
  const Badge = badge && BADGE[badge];
  return (
    <span className={`mt-art mt-art--${size}`} aria-hidden="true">
      <Art {...value} />
      {Badge && (
        <span className="mt-art__badge">
          <Badge />
        </span>
      )}
    </span>
  );
};
